import { PasswordInput } from '@/components/forms/password-input'
import { Input } from '@/components/forms/input'
import { AuthLayout } from '@/components/layout/auth-layout'
import { Button } from '@/components/primitives/button'
import type { PortalKind } from '@/lib/auth/token-storage'
import { AuthBrandHeader } from '@/features/auth/components/brand-header'
import { KeyIcon, LockIcon, MailIcon } from '@/features/auth/components/icons'
import { LanguageSegmentedControl } from '@/features/auth/components/language-segmented-control'
import { UserMobileLoginPage } from '@/features/auth/mobile/user-mobile-login-page'
import { usePortalLoginPage } from '@/features/auth/login/use-portal-login-page'
import { setAppLanguage } from '@/i18n/i18n'
import { useMediaQuery } from '@/lib/use-media-query'

interface AuthPageProps {
  loginMode?: 'default' | 'password'
  portal: PortalKind
}

export function AuthPage({ loginMode = 'default', portal }: AuthPageProps) {
  const isMobile = useMediaQuery('(max-width: 767px)')
  if (portal === 'user' && isMobile) return <UserMobileLoginPage loginMode={loginMode} />
  return <AuthDesktopPage loginMode={loginMode} portal={portal} />
}

function AuthDesktopPage({ loginMode = 'default', portal }: AuthPageProps) {
  const {
    clearFieldFeedback,
    errors,
    isUserPortal,
    language,
    loginMutation,
    loginEntryPending,
    noLoginMethods,
    onSubmit,
    register,
    shouldShrinkUserLoginCard,
    showEmailPasswordForm,
    showLoginDivider,
    showSsoLogin,
    ssoMutation,
    t,
    validateEmailOnBlur,
    validatePasswordOnBlur,
  } = usePortalLoginPage(portal, false, loginMode)

  return (
    <AuthLayout
      actions={
        <LanguageSegmentedControl
          language={language}
          onLanguageChange={(nextLanguage) => {
            void setAppLanguage(nextLanguage)
          }}
          t={t}
        />
      }
      portal={portal}
      cardClassName={shouldShrinkUserLoginCard ? '!min-h-0' : undefined}
    >
      <div className="grid">
        <AuthBrandHeader title={t(`${portal}.title`)} />

        {loginEntryPending ? (
          <div className="mt-8 grid justify-items-center gap-4 py-10 text-center">
            <div className="size-10 animate-spin rounded-full border-2 border-[rgba(25,91,255,0.16)] border-t-[var(--color-primary)]" />
            <p className="text-sm leading-6 text-[var(--color-text-muted)]">
              {t('user.redirectingToSso')}
            </p>
          </div>
        ) : showEmailPasswordForm ? (
          <form
            className={isUserPortal ? 'mt-[30px] grid gap-5' : 'mt-8 grid gap-5'}
            onSubmit={onSubmit}
          >
            <div className="grid gap-1.5">
              <label className="text-xs leading-none text-[#374151]" htmlFor={`${portal}-email`}>
                {t('common.emailLabel')}
              </label>
              <Input
                id={`${portal}-email`}
                invalid={Boolean(errors.email)}
                placeholder={t('common.emailPlaceholder')}
                prefix={<MailIcon />}
                type="email"
                className="h-[42px] rounded-lg border-[var(--color-control-border)] text-sm"
                {...register('email', {
                  onBlur: validateEmailOnBlur,
                  onChange: () => {
                    clearFieldFeedback('email')
                  },
                })}
              />
              {errors.email?.message ? (
                <p className="text-xs text-[var(--color-danger)]">{errors.email.message}</p>
              ) : null}
            </div>

            <div className="grid gap-1.5">
              <label className="text-xs leading-none text-[#374151]" htmlFor={`${portal}-password`}>
                {t('common.passwordLabel')}
              </label>
              <PasswordInput
                id={`${portal}-password`}
                invalid={Boolean(errors.password)}
                placeholder={t('common.passwordPlaceholder')}
                prefix={<LockIcon />}
                className="h-[42px] rounded-lg border-[var(--color-control-border)] text-sm"
                hideLabel={t('common.hidePassword')}
                showLabel={t('common.showPassword')}
                {...register('password', {
                  onBlur: validatePasswordOnBlur,
                  onChange: () => {
                    clearFieldFeedback('password')
                  },
                })}
              />
              {errors.password?.message ? (
                <p className="text-xs text-[var(--color-danger)]">{errors.password.message}</p>
              ) : null}
            </div>

            <Button
              className="h-[42px] rounded-lg text-base font-bold"
              disabled={loginMutation.isPending}
              isLoading={loginMutation.isPending}
              type="submit"
            >
              {t(`${portal}.submit`)}
            </Button>
          </form>
        ) : null}

        {!loginEntryPending && (errors.root?.message || noLoginMethods) ? (
          <p
            role="alert"
            className="mt-5 break-words rounded-md bg-red-50 px-3 py-2 text-xs leading-5 text-[var(--color-danger)]"
          >
            {errors.root?.message ?? t('user.noLoginMethods')}
          </p>
        ) : null}

        {!loginEntryPending && showSsoLogin ? (
          <div className="mt-5 grid gap-5">
            {showLoginDivider ? (
              <div className="flex items-center gap-4 text-xs text-[rgba(26,28,25,0.32)]">
                <div className="h-px flex-1 rounded-full bg-[var(--color-divider)]" />
                <span>{t('user.divider')}</span>
                <div className="h-px flex-1 rounded-full bg-[var(--color-divider)]" />
              </div>
            ) : null}
            <Button
              className="h-[42px] rounded-lg !border-[rgba(25,91,255,0.3)] !bg-[rgba(25,91,255,0.05)] text-sm font-normal !text-[var(--color-primary)] hover:!bg-[rgba(25,91,255,0.09)]"
              isLoading={ssoMutation.isPending}
              onClick={() => ssoMutation.mutate()}
              type="button"
              variant="secondary"
            >
              <KeyIcon />
              {t('user.sso')}
            </Button>
          </div>
        ) : null}
      </div>
    </AuthLayout>
  )
}
